import { apiFetch } from './http';
import type { IdentityDetail, IdentityProvider, Role } from './auth';
import type { DiscordRefusal } from './changelog';
import type { RecipeState } from './missions';

export interface AccountView {
  id: number;
  handle: string;
  displayName: string;
  roles: Role[];
  identities: IdentityProvider[];
  identityDetails: IdentityDetail[];
  createdAt: string;
  /** The last time the account came in through any door, not only the password form. */
  lastSeenAt: string | null;
  disabled: boolean;
  onboarded: boolean;
  /** The picture of the account's Steam identity; absent while the platform has not fetched it. */
  avatarUrl?: string | null;
}

export interface SignInSettingsView {
  steamEnabled: boolean;
  /** Whether a Steam sign-in opens an account by itself for a uid the game has already seen play. */
  steamPlayersMayJoin: boolean;
  discordEnabled: boolean;
  discordClientId: string;
  discordSecretSet: boolean;
  discordConfigured: boolean;
  /** Read back empty when one is stored; the flag beside it says whether there is one. */
  serverAdminPassword?: string;
  serverAdminPasswordSet?: boolean;
  serverPlayerPassword?: string;
  serverPlayerPasswordSet?: boolean;
  /**
   * The panel server a new one is copied from — difficulty, network settings and
   * options. Null means the panel's own global defaults. An id, not a secret, so
   * unlike the passwords it is read back.
   */
  referenceServerId?: number | null;
}

export interface PlayerView {
  uid: string;
  name: string;
  firstSeen: string;
  /** The last time the linked account signed in; null while no account is linked. */
  lastSeen: string | null;
  /** The last match this Steam id appears in; null while it has played none. */
  lastPlayedAt?: string | null;
  seenCount: number;
  accountId: number | null;
  handle: string | null;
  verified: boolean | null;
  /** This Steam id's picture; absent while the platform has not fetched it. */
  avatarUrl?: string | null;
}

export const fetchSettings = () => apiFetch<SignInSettingsView>('/api/admin/settings');
export const updateSettings = (change: {
  steamEnabled?: boolean;
  steamPlayersMayJoin?: boolean;
  discordEnabled?: boolean;
  discordClientId?: string;
  discordClientSecret?: string;
  serverAdminPassword?: string;
  serverPlayerPassword?: string;
  /** 0 clears it; leaving it out changes nothing. */
  referenceServerId?: number;
}) => apiFetch<SignInSettingsView>('/api/admin/settings', { method: 'PUT', body: change });
/** The platform's own cba_settings.sqf, shipped with every generated mission (2026-09-14). */
export interface CbaSettingsView {
  text: string;
  lines: number;
  updatedAt: string | null;
  updatedBy: number | null;
  /** How many open sessions' folders a save rewrote; null on a read. */
  republished: number | null;
}
export const fetchCbaSettings = () => apiFetch<CbaSettingsView>('/api/admin/cba-settings');
export const updateCbaSettings = (text: string) =>
  apiFetch<CbaSettingsView>('/api/admin/cba-settings', { method: 'PUT', body: { text } });

export const fetchPlayers = () => apiFetch<PlayerView[]>('/api/admin/players');
export const linkPlayer = (uid: string, accountId: number) =>
  apiFetch<PlayerView[]>(`/api/admin/players/${uid}/link`, { method: 'POST', body: { accountId } });
export const unlinkPlayer = (uid: string) =>
  apiFetch<PlayerView[]>(`/api/admin/players/${uid}/link`, { method: 'DELETE' });
export const setAccountIdentity = (id: number, provider: 'STEAM' | 'DISCORD', subject: string) =>
  apiFetch<AccountView>(`/api/admin/accounts/${id}/identities`, {
    method: 'POST',
    body: { provider, subject },
  });
export const removeAccountIdentity = (id: number, provider: 'STEAM' | 'DISCORD') =>
  apiFetch<AccountView>(`/api/admin/accounts/${id}/identities/${provider}`, { method: 'DELETE' });

export interface ActivityView {
  id: number;
  accountId: number | null;
  kind: string;
  subject: string | null;
  detail: string | null;
  occurredAt: string;
  /** The same event written for a person, in Catalan; null for a kind the platform has no sentence for. */
  text: string | null;
}

export const GRANTABLE_ROLES: Role[] = [
  'EDITOR',
  'CURATOR',
  'SERVER_MANAGER',
  'PANEL_ACCESS',
  'TESTER',
  'ADMIN',
];

export function fetchAccounts(): Promise<AccountView[]> {
  return apiFetch<AccountView[]>('/api/admin/accounts');
}

export function createAccount(input: {
  handle: string;
  displayName: string;
  password: string;
}): Promise<AccountView> {
  return apiFetch<AccountView>('/api/admin/accounts', { method: 'POST', body: input });
}

export function setRole(id: number, role: Role, granted: boolean): Promise<AccountView> {
  return apiFetch<AccountView>(`/api/admin/accounts/${id}/roles`, {
    method: 'POST',
    body: { role, granted },
  });
}

export function setDisabled(id: number, disabled: boolean): Promise<AccountView> {
  return apiFetch<AccountView>(`/api/admin/accounts/${id}/disabled`, {
    method: 'POST',
    body: { disabled },
  });
}

/** A part of the log, newest first; `more` says an older part waits, asked for with `before` = the last id here. */
export interface ActivityPage {
  entries: ActivityView[];
  more: boolean;
}

/** What the log is cut to: an account (what it did and what was done to it), a kind, the part before an id. */
export interface ActivityQuery {
  account?: number;
  kind?: string;
  before?: number;
}

export function fetchActivity(query: ActivityQuery = {}): Promise<ActivityPage> {
  const params = new URLSearchParams();
  if (query.account != null) params.set('account', String(query.account));
  if (query.kind) params.set('kind', query.kind);
  if (query.before != null) params.set('before', String(query.before));
  const qs = params.toString();
  return apiFetch<ActivityPage>(`/api/admin/activity${qs ? `?${qs}` : ''}`);
}

/** One server on the panel, as the admin's panel card lists it. */
export interface PanelCardServer {
  id: number;
  name: string;
  /** What the platform may do with it: it created it, was lent it, or only reads it. */
  access: 'MANAGED' | 'LINKED' | 'NONE';
  /** The panel account that created it; null for the operator's own. */
  createdBy: string | null;
}

export interface PanelCardView {
  configured: boolean;
  /** Whether the panel answered the call this very request made. */
  reachable: boolean;
  /** The last time it answered anything; null if not since the platform started. */
  lastAnsweredAt: string | null;
  lastFailure: string | null;
  authMode: 'credentials' | 'token' | 'none';
  serviceAccount: string | null;
  servers: PanelCardServer[];
  /** The last failed calls, newest first. */
  recentFailures: { at: string; reason: string }[];
  checkedAt: string;
}

/** The panel as the platform sees it; an answer even when the panel does not. */
export function fetchPanelCard(): Promise<PanelCardView> {
  return apiFetch<PanelCardView>('/api/admin/panel');
}

export interface DiscordTest {
  configured: boolean;
  ok: boolean;
  channels: {
    channel: string;
    configured: boolean;
    ok: boolean;
    /** Which of the two ways this channel used: `bot`, `webhook` or `none`. */
    transport?: string;
    /** What Discord said when it refused; null when it took the line or nobody tried. */
    discord: DiscordRefusal | null;
  }[];
}

export function testDiscord(): Promise<DiscordTest> {
  return apiFetch<DiscordTest>('/api/admin/discord/test', { method: 'POST' });
}

/** The recovery path while no e-mail exists: an admin sets a member's new password and hands it over. */
export function resetPassword(id: number, password: string): Promise<void> {
  return apiFetch<void>(`/api/admin/accounts/${id}/password`, {
    method: 'POST',
    body: { password },
  });
}

/** Study 6.4 "nothing is deleted": the row stays, the person leaves it; every open sign-in of the account ends. */
export function anonymiseAccount(id: number): Promise<AccountView> {
  return apiFetch<AccountView>(`/api/admin/accounts/${id}/anonymise`, { method: 'POST' });
}

/** One invitation link, as the admin page shows it. The code travels only inside `url`. */
export interface InvitationView {
  id: number;
  code: string;
  /** The whole address to hand out: `<public base>/register?invite=<code>`. */
  url: string;
  note: string | null;
  createdAt: string;
  expiresAt: string | null;
  /** How many registrations it may open; zero means as many as needed. */
  maxUses: number;
  uses: number;
  revoked: boolean;
  /** False once it is withdrawn, spent or past its date. */
  usable: boolean;
}

export const fetchInvitations = () => apiFetch<InvitationView[]>('/api/admin/invitations');
export const createInvitation = (input: {
  note?: string;
  maxUses?: number;
  expiresInDays?: number;
}) => apiFetch<InvitationView>('/api/admin/invitations', { method: 'POST', body: input });
export const revokeInvitation = (id: number) =>
  apiFetch<InvitationView>(`/api/admin/invitations/${id}`, { method: 'DELETE' });

/** One recipe as the admin page keeps it (AdminRecipeController.Row). */
export interface AdminRecipeRow {
  id: string;
  title: string;
  icon: string | null;
  /** False for a planned one: its state is fixed and nothing about it is set. */
  generatable: boolean;
  /** What the code declares. */
  declared: RecipeState;
  /** The state in force. */
  state: RecipeState;
  /** Whether an admin decided the state; false while it follows the code. */
  decided: boolean;
  cardsRequired: number;
  /** Passed and failed test cards since the state was last decided (all of them when it never was). */
  testsPassed: number;
  testsFailed: number;
  /** Distinct testers who are not admins and played a passed card. */
  testers: number;
  /** What the platform proposes; it never changes a state by itself. */
  proposal: 'ready' | 'tests' | null;
  note: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
}

export interface AdminRecipeListing {
  recipes: AdminRecipeRow[];
  /** Closed test cards that name no mission a recipe can be read from: counted for no recipe. */
  untiedCards: number;
}

export function fetchAdminRecipes(): Promise<AdminRecipeListing> {
  return apiFetch<AdminRecipeListing>('/api/admin/recipes');
}

/** A partial change: a field left out is left alone. Answers the row as it now stands. */
export function updateAdminRecipe(
  id: string,
  change: { state?: RecipeState; cardsRequired?: number; note?: string },
): Promise<AdminRecipeRow> {
  return apiFetch<AdminRecipeRow>(`/api/admin/recipes/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: change,
  });
}
